/**
 * Navigation icons.
 *
 * Drawn here rather than pulled from an icon font. The old interface loaded
 * FontAwesome from a CDN, which is a poor fit for software whose whole premise
 * is that it keeps working when the internet does not — the first thing a
 * branch would lose during an outage is every icon on the screen.
 *
 * Stroked in currentColor so they take the colour of whatever they sit in,
 * and sized by the parent so one set works at any scale.
 */
const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
};

/** A till: a drawer with a display above it. */
export function TillIcon(props) {
  return (
    <svg {...base} {...props}>
      <rect x="3" y="10" width="18" height="11" rx="2" />
      <path d="M7 10V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v5" />
      <path d="M9 15h6" />
    </svg>
  );
}

/** A flame, for the kitchen. */
export function KitchenIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M12 3c0 3-3 4-3 7a3 3 0 0 0 6 0c0-1-.5-2-1-2.5" />
      <path d="M8.5 12A5.5 5.5 0 0 0 12 21a5.5 5.5 0 0 0 3.5-9" />
    </svg>
  );
}

/** A receipt with a torn edge. */
export function OrdersIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9 8h6M9 12h6" />
    </svg>
  );
}

/** Fork and knife. */
export function MenuIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M7 3v8a2 2 0 0 0 4 0V3M9 11v10" />
      <path d="M17 3c-1.5 1.5-2 3-2 5s.5 2.5 2 2.5V21" />
    </svg>
  );
}

/** Stacked boxes. */
export function StockIcon(props) {
  return (
    <svg {...base} {...props}>
      <rect x="3" y="12" width="8" height="8" rx="1" />
      <rect x="13" y="12" width="8" height="8" rx="1" />
      <rect x="8" y="4" width="8" height="8" rx="1" />
    </svg>
  );
}

/** A delivery van. */
export function PurchasingIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M3 7h11v9H3z" />
      <path d="M14 10h4l3 3v3h-7z" />
      <circle cx="7" cy="18" r="1.75" />
      <circle cx="17" cy="18" r="1.75" />
    </svg>
  );
}

/** Bars, for reports. */
export function ReportsIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  );
}

/** A scooter, for delivery — the same thing the logo is. */
export function DeliveryIcon(props) {
  return (
    <svg {...base} {...props}>
      <circle cx="5.5" cy="17" r="3" />
      <circle cx="18.5" cy="17" r="3" />
      <path d="M8.5 17h7" />
      <path d="M15.5 17V9h-4l-2 4" />
      <path d="M15.5 9h3l2 5" />
    </svg>
  );
}

/** A takeaway bag, for the Foodpanda tab. */
export function FoodpandaIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M6 8h12l-1 11a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 8z" />
      <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    </svg>
  );
}

/** A banknote, for money going out. */
export function ExpensesIcon(props) {
  return (
    <svg {...base} {...props}>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 12h.01M18 12h.01" />
    </svg>
  );
}

/** A bank building, for reconciliation. */
export function BankingIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M3 10h18M4 10 12 4l8 6" />
      <path d="M6 10v7M10 10v7M14 10v7M18 10v7" />
      <path d="M3 20h18" />
    </svg>
  );
}

/** A ledger page with a rule down it, for vouchers. */
export function VouchersIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M5 3h14v18H5z" />
      <path d="M14 3v18" />
      <path d="M8 8h3M8 12h3M8 16h3" />
    </svg>
  );
}

/** Tables seen from above, for the floor. */
export function FloorIcon(props) {
  return (
    <svg {...base} {...props}>
      <circle cx="8" cy="8" r="3" />
      <circle cx="16" cy="16" r="3" />
      <rect x="13" y="5" width="6" height="6" rx="1" />
      <rect x="5" y="13" width="6" height="6" rx="1" />
    </svg>
  );
}

/** A shopfront, for branches. */
export function BranchesIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M3 9h18l-1.5-4.5A2 2 0 0 0 17.6 3H6.4a2 2 0 0 0-1.9 1.5z" />
      <path d="M4 9v11h16V9" />
      <path d="M10 20v-6h4v6" />
    </svg>
  );
}

/** People, for staff accounts. */
export function StaffIcon(props) {
  return (
    <svg {...base} {...props}>
      <circle cx="9" cy="8" r="3.25" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 5.5a3 3 0 0 1 0 5.5M17 20a5.5 5.5 0 0 0-2-4.3" />
    </svg>
  );
}

/** The people the shop serves, as opposed to the people who work in it. */
export function CustomersIcon(props) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
      <path d="M12 11.5v0" />
    </svg>
  );
}

/** The rules the shop runs on, rather than the work of a shift. */
export function SettingsIcon(props) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </svg>
  );
}

/** A chevron, for collapsing the sidebar. */
export function ChevronIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M15 6l-6 6 6 6" />
    </svg>
  );
}

/** An approval: a check inside a shield. */
export function ApprovalsIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M12 3l7 4v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V7l7-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}
